import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/** Socket ids reserved by the step itself; user-defined classifications cannot use them. */
export const RESERVED_SPLIT_CLASSIFICATION_IDS = ["all_documents"] as const;

const splitClassificationSchema = z.object({
  id: z
    .string()
    .regex(
      /^[a-z][a-z0-9_]*$/,
      "Classification id must be lowercase alphanumeric with underscores (valid socket id)",
    )
    .refine(
      (id) =>
        !(RESERVED_SPLIT_CLASSIFICATION_IDS as readonly string[]).includes(id),
      {
        message: `Classification id cannot be one of the reserved socket ids: ${RESERVED_SPLIT_CLASSIFICATION_IDS.join(", ")}`,
      },
    )
    .describe(
      "Unique identifier for this classification (e.g. 'purchase_order', 'invoice', 'bill_of_lading'). Becomes an output socket id — must be lowercase snake_case.",
    ),
  type: z
    .string()
    .min(1)
    .describe(
      "Classification type label sent to the splitter (e.g. 'PURCHASE_ORDER', 'INVOICE', 'BILL_OF_LADING')",
    ),
  description: z
    .string()
    .optional()
    .describe(
      "Human-readable description that helps the AI identify this document type (e.g. 'A purchase order from a retailer listing items, quantities, and prices')",
    ),
});

/** Values accepted by Extend's split run API (`advancedOptions.splitMethod`). */
export const SPLIT_METHODS = [
  "basic_precision",
  "high_precision",
  "low_latency",
] as const;
export type SplitMethod = (typeof SPLIT_METHODS)[number];

export const SPLIT_METHOD_LABELS: Record<SplitMethod, string> = {
  basic_precision: "Basic precision",
  high_precision: "High precision",
  low_latency: "Low latency",
};

/** @deprecated Persisted configs may still use `standard`; map to `basic_precision` when calling Extend. */
const LEGACY_SPLIT_METHOD = "standard" as const;

const splitMethodSchema = z.union([
  z.enum(SPLIT_METHODS),
  z.literal(LEGACY_SPLIT_METHOD),
]);

/**
 * Split Document V2 Step
 *
 * Generic, provider-agnostic document splitter. Takes a single document,
 * splits it into child documents using a configurable provider (Extend AI
 * by default), and outputs the resulting child document references.
 *
 * Classifications are fully user-defined via the UI — no hardcoded document
 * types. Documents that cannot be split (unsupported format, no matching
 * splits, provider error) are returned as-is so downstream steps always
 * have input.
 */
export const splitDocumentV2Step = defineStepConfig({
  type: "split_document_v2",
  name: "Split Document (V2)",
  description:
    "Split a multi-page document into individual child documents using a configurable splitter provider",
  category: "AI & Agents",

  inputSchema: z.object({
    document: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "The document to split into child documents",
    ),
  }),

  // Output sockets are generated dynamically at template instantiation from
  // config.customClassifications (one array socket per classification id),
  // plus a reserved `all_documents` socket carrying rich classification data.
  // Uses z.record() so that dynamic keys survive Zod validation (z.object()
  // would strip unknown keys). Same trick switchStep uses.
  outputSchema: z.record(z.string(), z.any()),

  configSchema: z.object({
    customClassifications: z
      .array(splitClassificationSchema)
      .min(1)
      .optional()
      .describe(
        "User-defined split classifications. Each entry tells the splitter what type of sub-document to look for.",
      ),
    splitRules: z
      .string()
      .optional()
      .describe(
        "Custom rules or prompt instructions that guide how the splitter divides the document.",
      ),
    advancedOptions: z
      .object({
        splitMethod: splitMethodSchema.optional(),
      })
      .optional()
      .describe("Provider-specific tuning options."),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 1,
    },
  },
});
